import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useOrders } from './OrderContext';

const FinanceContext = createContext();

export const useFinance = () => {
  const context = useContext(FinanceContext);
  if (!context) {
    throw new Error('useFinance must be used within FinanceProvider');
  }
  return context;
};

export const FinanceProvider = ({ children }) => {
  const { orders } = useOrders();
  const [payouts, setPayouts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to payouts
  useEffect(() => {
    const q = query(collection(db, 'payouts'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPayouts(items);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching payouts:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Subscribe to expenses
  useEffect(() => {
    const q = query(collection(db, 'expenses'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setExpenses(items);
    }, (error) => {
      console.error('Error fetching expenses:', error);
    });

    return () => unsubscribe();
  }, []);

  // Subscribe to invoices
  useEffect(() => {
    const q = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInvoices(items);
    }, (error) => {
      console.error('Error fetching invoices:', error);
    });

    return () => unsubscribe();
  }, []);

  // Calculate current payout cycle (weekly cycle ending on Sunday)
  const currentCycle = useMemo(() => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = Sunday

    // Calculate start of current week (Monday)
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    startDate.setHours(0, 0, 0, 0);

    // Calculate end of current week (Sunday)
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    endDate.setHours(23, 59, 59, 999);

    // Payout date is typically 2-3 days after cycle ends
    const payoutDate = new Date(endDate);
    payoutDate.setDate(endDate.getDate() + 3);

    // Calculate orders in this cycle
    const cycleOrders = orders.filter(order => {
      const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
      return orderDate >= startDate && orderDate <= endDate && order.status === 'delivered';
    });

    const estimatedPayout = cycleOrders.reduce((sum, order) => {
      return sum + (order.total || order.totalAmount || 0);
    }, 0);

    return {
      startDate,
      endDate,
      payoutDate,
      orderCount: cycleOrders.length,
      estimatedPayout,
      status: 'pending'
    };
  }, [orders]);

  // Get transactions from orders
  const orderTransactions = useMemo(() => {
    return orders.map(order => ({
      id: order.id,
      orderId: order.orderNumber || order.id?.slice(-6),
      status: order.status,
      date: order.createdAt,
      paymentMethod: order.paymentMethod,
      grossRevenue: order.total || order.totalAmount || 0,
      commission: Math.round((order.total || order.totalAmount || 0) * 0.05), // 5% commission
      netReceivable: Math.round((order.total || order.totalAmount || 0) * 0.95),
      settled: order.status === 'delivered'
    }));
  }, [orders]);

  // Add payout record
  const addPayout = async (payoutData) => {
    try {
      const newPayout = {
        ...payoutData,
        createdAt: Timestamp.now(),
        status: 'pending'
      };
      const docRef = await addDoc(collection(db, 'payouts'), newPayout);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error adding payout:', error);
      return { success: false, error };
    }
  };

  // Add expense
  const addExpense = async (expenseData) => {
    try {
      const newExpense = {
        ...expenseData,
        createdAt: Timestamp.now()
      };
      const docRef = await addDoc(collection(db, 'expenses'), newExpense);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error adding expense:', error);
      return { success: false, error };
    }
  };

  // Get finance stats
  const getFinanceStats = () => {
    const deliveredOrders = orders.filter(o => o.status === 'delivered');
    const totalRevenue = deliveredOrders.reduce((sum, o) => sum + (o.total || o.totalAmount || 0), 0);
    const totalCommission = Math.round(totalRevenue * 0.05);
    const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    const netEarnings = totalRevenue - totalCommission - totalExpenses;

    const totalPaidOut = payouts
      .filter(p => p.status === 'paid')
      .reduce((sum, p) => sum + (p.amount || 0), 0);

    return {
      totalRevenue,
      totalCommission,
      totalExpenses,
      netEarnings,
      totalPaidOut,
      pendingPayout: netEarnings - totalPaidOut
    };
  };

  // Filter transactions by date range
  const filterTransactionsByDate = (startDate, endDate) => {
    return orderTransactions.filter(t => {
      const txDate = t.date?.toDate ? t.date.toDate() : new Date(t.date);
      return txDate >= startDate && txDate <= endDate;
    });
  };

  // Filter payouts by date range
  const filterPayoutsByDate = (startDate, endDate) => {
    return payouts.filter(p => {
      const pDate = p.createdAt?.toDate ? p.createdAt.toDate() : new Date(p.createdAt);
      return pDate >= startDate && pDate <= endDate;
    });
  };

  const value = {
    payouts,
    transactions: orderTransactions,
    expenses,
    invoices,
    loading,
    currentCycle,
    addPayout,
    addExpense,
    getFinanceStats,
    filterTransactionsByDate,
    filterPayoutsByDate
  };

  return (
    <FinanceContext.Provider value={value}>
      {children}
    </FinanceContext.Provider>
  );
};
